'use client';

import * as React from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../forms/button';
import { Textarea } from '../forms/textarea';

/**
 * HilApprovalCard — human-in-the-loop approval for deep-agent tool calls.
 * The agent pauses and asks the user to approve / edit / reject one or more
 * proposed actions before they run. Sits inline in the assistant line
 * (S2 full column or S3 docked panel — adapts via container query).
 *
 * States: 대기(interactive) → 제출됨(locked / pending) → 결정 완료(done-tone trace).
 * Token-only; the resolved state matches the `done` activity-trace tone.
 */

export type HilDecision = 'approve' | 'edit' | 'reject';

export interface HilAction {
  /** Tool / action name (rendered in mono). */
  name: string;
  /** Proposed arguments; shown as JSON, editable on `edit`. */
  args?: unknown;
  /** Which decisions are offered for this action (buttons are gated by this). */
  allowedDecisions: HilDecision[];
  /** Optional one-line human summary of what the action does. */
  summary?: string;
}

export interface HilDecisionResult {
  name: string;
  decision: HilDecision;
  /** Edited args (only when decision === "edit"). */
  args?: unknown;
  /** Rejection reason (only when decision === "reject"). */
  reason?: string;
}

export interface HilApprovalCardProps {
  actions: HilAction[];
  onDecide?: (decisions: HilDecisionResult[]) => void;
  /** External lock while the backend processes the submission. */
  pending?: boolean;
  title?: string;
  mode?: 'full' | 'panel' | 'auto';
  className?: string;
}

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const PATHS: Record<string, React.ReactNode> = {
  shield: (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,
  pencil: (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </>
  ),
  x: (
    <>
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </>
  ),
};

function Ico({ name, size = 16, className }: { name: keyof typeof PATHS; size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      {...STROKE}
      className={cn('block flex-none', className)}
      aria-hidden="true"
    >
      {PATHS[name]}
    </svg>
  );
}

const DEC_META: Record<HilDecision, { label: string; icon: keyof typeof PATHS }> = {
  approve: { label: '승인', icon: 'check' },
  edit: { label: '수정', icon: 'pencil' },
  reject: { label: '거부', icon: 'x' },
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v != null && typeof v === 'object' && !Array.isArray(v);

const isFlat = (obj: unknown): obj is Record<string, unknown> =>
  isRecord(obj) && Object.values(obj).every((v) => v == null || typeof v !== 'object');

interface RowState {
  dec: HilDecision | null;
  reason: string;
  jsonMode: boolean;
  jsonText: string;
  jsonError: string | null;
  form: Record<string, unknown>;
}

export function HilApprovalCard({
  actions = [],
  onDecide,
  pending = false,
  title = '승인 요청',
  mode = 'auto',
  className,
}: HilApprovalCardProps) {
  const [rows, setRows] = React.useState<RowState[]>(() =>
    actions.map((a) => ({
      dec: null,
      reason: '',
      jsonMode: !isFlat(a.args),
      jsonText: JSON.stringify(a.args ?? {}, null, 2),
      jsonError: null,
      form: isRecord(a.args) ? { ...a.args } : {},
    })),
  );
  const [submitted, setSubmitted] = React.useState(false);
  const [result, setResult] = React.useState<HilDecisionResult[] | null>(null);

  const setRow = (i: number, patch: Partial<RowState>) =>
    setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...patch } : r)));

  const rowValid = (a: HilAction, r: RowState): boolean => {
    if (!r.dec) return false;
    if (r.dec === 'reject') return r.reason.trim().length > 0;
    if (r.dec === 'edit' && r.jsonMode) return !r.jsonError;
    return true;
  };
  const canSubmit = actions.length > 0 && actions.every((a, i) => rowValid(a, rows[i]!));
  const decidedCount = rows.filter((r) => r.dec).length;
  const allApprovable = actions.every((a) => a.allowedDecisions.includes('approve'));

  const editArgs = (a: HilAction, r: RowState): unknown => {
    if (r.dec !== 'edit') return undefined;
    if (r.jsonMode) {
      try {
        return JSON.parse(r.jsonText);
      } catch {
        return a.args;
      }
    }
    return r.form;
  };

  const submit = () => {
    if (!canSubmit) return;
    const payload: HilDecisionResult[] = actions.map((a, i) => {
      const r = rows[i]!;
      const d: HilDecisionResult = { name: a.name, decision: r.dec! };
      if (r.dec === 'edit') d.args = editArgs(a, r);
      if (r.dec === 'reject') d.reason = r.reason.trim();
      return d;
    });
    setResult(payload);
    setSubmitted(true);
    onDecide?.(payload);
  };

  const approveAll = () =>
    setRows((rs) =>
      rs.map((r, i) => (actions[i]!.allowedDecisions.includes('approve') ? { ...r, dec: 'approve' } : r)),
    );

  const maxWidth = mode === 'panel' ? 360 : 560;

  const cardCls = cn(
    '@container w-full overflow-hidden font-sans text-fg',
    'rounded-[var(--radius-card)] border border-border bg-surface shadow-[var(--shadow-card)]',
    className,
  );

  // ---- Resolved state: done-tone decision trace ----
  if (submitted && !pending && result) {
    const meta: Record<HilDecision, { ic: keyof typeof PATHS; col: string; txt: string }> = {
      approve: { ic: 'check', col: 'text-[var(--color-status-done-icon)]', txt: '승인함' },
      edit: { ic: 'pencil', col: 'text-[var(--color-accent)]', txt: '수정 후 승인함' },
      reject: { ic: 'x', col: 'text-[var(--color-status-error-fg)]', txt: '거부함' },
    };
    return (
      <div className={cardCls} style={{ maxWidth }}>
        <div className="flex items-center gap-[var(--space-2-5)] border-b border-border-subtle px-[var(--space-4)] py-[var(--space-3)]">
          <span className="flex size-[30px] items-center justify-center rounded-full bg-[var(--color-success-soft)]">
            <Ico name="check" size={17} className="text-[var(--color-success)]" />
          </span>
          <div className="text-md font-semibold">결정 완료</div>
        </div>
        <div className="px-[var(--space-4)] py-[var(--space-3)]">
          <div className="ml-[var(--status-rail-indent)] flex flex-col gap-[var(--status-line-gap)] border-l-[1.5px] border-[var(--color-status-rail)] pl-[var(--status-rail-gap)]">
            {result.map((d, i) => (
              <div key={i} className="flex min-w-0 items-center gap-[var(--status-gap)]">
                <Ico name={meta[d.decision].ic} size={14} className={meta[d.decision].col} />
                <span className="truncate text-[length:var(--status-font)] text-[var(--color-status-done-fg)]">
                  <span className="font-mono text-fg-secondary">{d.name}</span> {meta[d.decision].txt}
                  {d.decision === 'reject' && d.reason ? ' · ' + d.reason : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const locked = submitted && pending;

  return (
    <div className={cardCls} style={{ maxWidth }}>
      {/* header */}
      <div className="flex items-center gap-[var(--space-2-5)] border-b border-border-subtle px-[var(--space-4)] py-[var(--space-3)]">
        <span className="flex size-[30px] flex-none items-center justify-center rounded-full bg-accent-soft">
          <Ico name="shield" size={17} className="text-accent" />
        </span>
        <div className="min-w-0 flex-1 text-md font-semibold">{title}</div>
        <span className="flex-none whitespace-nowrap rounded-full bg-muted px-[var(--space-2)] py-0.5 font-mono text-xs text-fg-muted">
          {actions.length}건
        </span>
      </div>

      {/* actions */}
      <div
        className={cn('transition-opacity duration-[var(--duration-fast)]', locked && 'pointer-events-none opacity-55')}
      >
        {actions.map((a, i) => {
          const r = rows[i]!;
          return (
            <div
              key={i}
              className={cn('px-[var(--space-4)] py-[var(--space-3-5,0.875rem)]', i && 'border-t border-border-subtle')}
            >
              <div className="flex flex-wrap items-baseline gap-[var(--space-2)]">
                <code className="font-mono text-sm font-semibold text-fg">{a.name}</code>
                {a.summary && <span className="text-sm text-fg-secondary">{a.summary}</span>}
              </div>

              {/* args preview (hidden while editing this row) */}
              {r.dec !== 'edit' && a.args != null && (
                <pre className="mt-[var(--space-2)] max-h-[108px] overflow-auto rounded-[var(--radius-sm)] border border-border-subtle bg-[var(--color-bg-inset)] px-[var(--space-2-5)] py-[var(--space-2)] font-mono text-xs leading-[1.5] text-fg-secondary">
                  {JSON.stringify(a.args, null, 2)}
                </pre>
              )}

              {/* decision buttons — gated by allowedDecisions */}
              <div className="mt-[var(--space-2-5)] flex flex-wrap gap-[var(--space-2)]">
                {(['approve', 'edit', 'reject'] as HilDecision[])
                  .filter((d) => a.allowedDecisions.includes(d))
                  .map((d) => {
                    const on = r.dec === d;
                    const variant = on
                      ? d === 'reject'
                        ? 'danger'
                        : d === 'approve'
                          ? 'subtle'
                          : 'secondary'
                      : 'ghost';
                    return (
                      <Button
                        key={d}
                        size="sm"
                        variant={variant}
                        onClick={() => setRow(i, { dec: on ? null : d })}
                        iconLeft={<Ico name={DEC_META[d].icon} size={14} />}
                        className={cn(on && d === 'edit' && 'border-accent text-accent')}
                      >
                        {DEC_META[d].label}
                      </Button>
                    );
                  })}
              </div>

              {/* edit panel */}
              {r.dec === 'edit' && (
                <div className="mt-[var(--space-2-5)]">
                  <div className="mb-[var(--space-1-5)] flex items-center justify-between">
                    <span className="font-mono text-xs uppercase tracking-[0.06em] text-fg-muted">args 수정</span>
                    {isFlat(a.args) && (
                      <button
                        type="button"
                        onClick={() => setRow(i, { jsonMode: !r.jsonMode })}
                        className="cursor-pointer border-none bg-transparent font-sans text-xs text-[var(--color-text-link)]"
                      >
                        {r.jsonMode ? '폼으로' : 'JSON으로'}
                      </button>
                    )}
                  </div>
                  {r.jsonMode ? (
                    <>
                      <Textarea
                        autoGrow
                        rows={Math.min(8, r.jsonText.split('\n').length)}
                        value={r.jsonText}
                        invalid={!!r.jsonError}
                        onChange={(e) => {
                          const v = e.target.value;
                          let err: string | null = null;
                          try {
                            JSON.parse(v);
                          } catch {
                            err = 'JSON 형식 오류';
                          }
                          setRow(i, { jsonText: v, jsonError: err });
                        }}
                        className="font-mono text-xs"
                      />
                      {r.jsonError && (
                        <div className="mt-[var(--space-1)] text-xs text-danger">{r.jsonError}</div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col gap-[var(--space-2)]">
                      {Object.keys(isRecord(a.args) ? a.args : {}).map((k) => (
                        <label key={k} className="flex flex-col gap-[3px]">
                          <span className="font-mono text-xs text-fg-secondary">{k}</span>
                          <input
                            value={String(r.form[k] ?? '')}
                            onChange={(e) => setRow(i, { form: { ...r.form, [k]: e.target.value } })}
                            className="h-[var(--control-height-sm)] rounded-control border border-border-strong bg-surface px-[var(--control-px)] font-sans text-sm text-fg outline-none focus:border-accent"
                          />
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* reject reason */}
              {r.dec === 'reject' && (
                <div className="mt-[var(--space-2-5)]">
                  <Textarea
                    rows={2}
                    placeholder="거부 사유를 입력해 주세요"
                    value={r.reason}
                    invalid={r.reason.trim() === ''}
                    onChange={(e) => setRow(i, { reason: e.target.value })}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* footer */}
      <div className="flex items-center justify-between gap-[var(--space-3)] border-t border-border-subtle bg-[var(--color-bg-surface-2)] px-[var(--space-4)] py-[var(--space-3)] @max-[380px]:flex-col @max-[380px]:items-stretch">
        {locked ? (
          <span className="inline-flex items-center gap-[var(--status-gap)] text-[length:var(--status-font)] text-[var(--color-status-fg)]">
            <svg
              width={16}
              height={16}
              viewBox="0 0 24 24"
              aria-hidden="true"
              className="animate-spin text-[var(--color-status-icon)] motion-reduce:animate-none"
            >
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
              <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
            제출됨 · 처리 중…
          </span>
        ) : (
          <>
            <span className="text-xs text-fg-muted">
              {decidedCount}/{actions.length} 결정됨
            </span>
            <span className="flex gap-[var(--space-2)] @max-[380px]:w-full [&>button]:@max-[380px]:flex-1">
              {allApprovable && (
                <Button size="sm" variant="ghost" onClick={approveAll}>
                  모두 승인
                </Button>
              )}
              <Button size="sm" variant="primary" disabled={!canSubmit} onClick={submit}>
                결정 제출
              </Button>
            </span>
          </>
        )}
      </div>
    </div>
  );
}
